/**
 * Guest Identity Hook - Tracks anonymous users for anti-abuse and analytics
 * 
 * Usage:
 * const { guestId, isVerified, isBlocked, trackAction } = useGuestIdentity();
 * 
 * // After CAPTCHA verification
 * await initSession(captchaToken);
 * 
 * // Track page views
 * trackAction('page_view', { page: '/breeds' });
 * 
 * // Merge to user after registration
 * await mergeToUser(userId);
 */

import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const GUEST_ID_KEY = 'aadc_guest_id';
const SESSION_CHECKED_KEY = 'aadc_guest_checked';

export function useGuestIdentity() {
  const [guestId, setGuestId] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [visitCount, setVisitCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Initialize or restore guest session
  useEffect(() => {
    const initGuest = async () => {
      try {
        // Check localStorage for existing guest ID
        const storedGuestId = localStorage.getItem(GUEST_ID_KEY);
        const sessionChecked = sessionStorage.getItem(SESSION_CHECKED_KEY);
        
        // If we already checked this session, just restore state
        if (sessionChecked && storedGuestId) {
          setGuestId(storedGuestId);
          setLoading(false);
          return;
        }
        
        // Create or restore session from backend
        const response = await fetch(`${API}/api/guest/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ guest_id: storedGuestId })
        });
        
        if (response.ok) {
          const data = await response.json();
          setGuestId(data.guest_id);
          setIsVerified(data.captcha_verified);
          setIsBlocked(data.is_blocked);
          setVisitCount(data.visit_count);
          
          // Store in localStorage for persistence
          localStorage.setItem(GUEST_ID_KEY, data.guest_id);
          sessionStorage.setItem(SESSION_CHECKED_KEY, 'true');
        }
      } catch (error) {
        console.error('Failed to init guest session:', error);
      } finally {
        setLoading(false);
      }
    };
    
    initGuest();
  }, []);

  // Initialize session with CAPTCHA token
  const initSession = useCallback(async (captchaToken) => {
    try {
      const storedGuestId = localStorage.getItem(GUEST_ID_KEY);
      
      const response = await fetch(`${API}/api/guest/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_id: storedGuestId,
          captcha_token: captchaToken
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGuestId(data.guest_id);
        setIsVerified(true);
        setIsBlocked(data.is_blocked);
        localStorage.setItem(GUEST_ID_KEY, data.guest_id);
        return data;
      }
    } catch (error) {
      console.error('Failed to init session with CAPTCHA:', error);
    }
    return null;
  }, []);

  // Track an action
  const trackAction = useCallback(async (actionType, actionData = {}) => {
    if (!guestId || isBlocked) return;
    
    try {
      await fetch(`${API}/api/guest/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_id: guestId,
          action_type: actionType,
          action_data: actionData
        })
      });
    } catch (error) {
      // Silently fail - don't break UX for tracking
      console.debug('Track failed:', error);
    }
  }, [guestId, isBlocked]);

  // Merge guest session to user after registration.
  // Auth is handled via httpOnly cookie (credentials: 'include').
  const mergeToUser = useCallback(async (userId) => {
    if (!guestId) return null;
    
    try {
      const response = await fetch(`${API}/api/guest/merge/${userId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ guest_id: guestId })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Clear guest ID after merge
        localStorage.removeItem(GUEST_ID_KEY);
        sessionStorage.removeItem(SESSION_CHECKED_KEY);
        setGuestId(null);
        return data;
      }
    } catch (error) {
      console.error('Failed to merge guest to user:', error);
    }
    return null;
  }, [guestId]);

  // Check if current visitor is allowed to perform actions
  const checkStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/guest/check?guest_id=${guestId || ''}`);
      if (response.ok) {
        const data = await response.json();
        setIsBlocked(!data.allowed);
        return data;
      }
    } catch (error) {
      console.error('Failed to check guest status:', error);
    }
    return { allowed: true };
  }, [guestId]);

  return {
    guestId,
    isVerified,
    isBlocked,
    visitCount,
    loading,
    initSession,
    trackAction,
    mergeToUser,
    checkStatus
  };
}

// Helper to get guest ID without the full hook (for non-component use)
export function getStoredGuestId() {
  return localStorage.getItem(GUEST_ID_KEY);
}

// Helper to track a page view (can be called from anywhere)
export async function trackPageView(page) {
  const guestId = localStorage.getItem(GUEST_ID_KEY);
  if (!guestId) return;
  
  try {
    await fetch(`${API}/api/guest/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        guest_id: guestId,
        action_type: 'page_view',
        action_data: { page, timestamp: new Date().toISOString() }
      })
    });
  } catch (error) {
    // Silent fail
  }
}
